// src/components/Music.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import * as Tone from "tone";
import YouTube from "react-youtube";
import playlistsManifest from "../assets/livestreaming/playlists.json";
import playlistItemsMap from "../assets/livestreaming/playlistItems.json";
import SEO from "./SEO";
import "./Music.css";

const PLAYLIST_ID = "PLlfpe0DDwzp1IweiwK-PEuoAZxseiYklW";
const DEFAULT_SESSION_VOLUME = 65;
const YT_PLAYER_OPTIONS = {
  height: "0",
  width: "0",
  playerVars: {
    autoplay: 0,
    controls: 0,
    disablekb: 1,
    modestbranding: 1,
    rel: 0,
  },
};

const playlistsMeta = Array.isArray(playlistsManifest) ? playlistsManifest : [];
const playlistItemsData =
  playlistItemsMap && typeof playlistItemsMap === "object" ? playlistItemsMap : {};

// Curated library with worship-friendly metadata so DJs can rapidly curate setlists
const musicLibrary = [
  {
    title: "Bensound - Acoustic Breeze",
    url: "https://www.bensound.com/bensound-music/bensound-acousticbreeze.mp3",
    category: "Prayerful",
    style: "Acoustic",
    mood: "Calm",
    bpm: 94,
    length: "2:37",
  },
  {
    title: "Bensound - Better Days",
    url: "https://www.bensound.com/bensound-music/bensound-betterdays.mp3",
    category: "Prayerful",
    style: "Acoustic",
    mood: "Hopeful",
    bpm: 96,
    length: "2:33",
  },
  {
    title: "Bensound - Creative Minds",
    url: "https://www.bensound.com/bensound-music/bensound-creativeminds.mp3",
    category: "Moments of Joy",
    style: "Corporate",
    mood: "Joyful",
    bpm: 122,
    length: "2:27",
  },
  {
    title: "Bensound - Energy",
    url: "https://www.bensound.com/bensound-music/bensound-energy.mp3",
    category: "Celebration",
    style: "Electronic",
    mood: "Energetic",
    bpm: 128,
    length: "2:59",
  },
  {
    title: "Bensound - Once Again",
    url: "https://www.bensound.com/bensound-music/bensound-onceagain.mp3",
    category: "Prayerful",
    style: "Piano",
    mood: "Reflective",
    bpm: 87,
    length: "3:28",
  },
  {
    title: "Bensound - Sunny",
    url: "https://www.bensound.com/bensound-music/bensound-sunny.mp3",
    category: "Moments of Joy",
    style: "Ukulele",
    mood: "Celebratory",
    bpm: 100,
    length: "2:20",
  },
  {
    title: "Bensound - The Lounge",
    url: "https://www.bensound.com/bensound-music/bensound-thelounge.mp3",
    category: "Arrival",
    style: "Lounge",
    mood: "Warm",
    bpm: 94,
    length: "3:16",
  },
  {
    title: "Bensound - Dreams",
    url: "https://www.bensound.com/bensound-music/bensound-dreams.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Soothing",
    bpm: 78,
    length: "2:38",
  },
  {
    title: "Bensound - Epic",
    url: "https://www.bensound.com/bensound-music/bensound-epic.mp3",
    category: "Celebration",
    style: "Orchestral",
    mood: "Victorious",
    bpm: 120,
    length: "2:58",
  },
  {
    title: "Incompetech - Carefree",
    url: "https://incompetech.com/music/royalty-free/mp3-royaltyfree/Carefree.mp3",
    category: "Arrival",
    style: "Folk",
    mood: "Bright",
    bpm: 104,
    length: "2:48",
  },
  {
    title: "Incompetech - Funkorama",
    url: "https://incompetech.com/music/royalty-free/mp3-royaltyfree/Funkorama.mp3",
    category: "Celebration",
    style: "Funk",
    mood: "Playful",
    bpm: 112,
    length: "2:16",
  },
  {
    title: "Incompetech - Sneaky Snitch",
    url: "https://incompetech.com/music/royalty-free/mp3-royaltyfree/SneakySnitch.mp3",
    category: "Moments of Joy",
    style: "Quirky",
    mood: "Light",
    bpm: 100,
    length: "2:51",
  },
  {
    title: "Incompetech - Impact Moderato",
    url: "https://incompetech.com/music/royalty-free/mp3-royaltyfree/ImpactModerato.mp3",
    category: "Celebration",
    style: "Strings",
    mood: "Bold",
    bpm: 120,
    length: "2:15",
  },
  {
    title: "Archive - Cylinder Five",
    url: "https://archive.org/download/ChrisZabriskie_070/Cylinder_Five.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Meditative",
    bpm: 70,
    length: "3:30",
  },
  {
    title: "Archive - The Ambient World",
    url: "https://archive.org/download/ChrisZabriskie_070/The_Ambient_World.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Atmospheric",
    bpm: 68,
    length: "3:16",
  },
  {
    title: "SoundHelix Song 1",
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
    category: "Celebration",
    style: "Electronic",
    mood: "Driving",
    bpm: 128,
    length: "6:13",
  },
  {
    title: "SoundHelix Song 3",
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
    category: "Arrival",
    style: "Instrumental",
    mood: "Positive",
    bpm: 125,
    length: "5:32",
  },
  {
    title: "Hochmuth - Bloch Prayer",
    url: "https://www.hochmuth.com/mp3/Bloch_Prayer.mp3",
    category: "Prayerful",
    style: "Strings",
    mood: "Sacred",
    bpm: 60,
    length: "3:21",
  },
  {
    title: "Hochmuth - Haydn Cello Concerto",
    url: "https://www.hochmuth.com/mp3/Haydn_Cello_Concerto_D-1.mp3",
    category: "Moments of Joy",
    style: "Classical",
    mood: "Elegant",
    bpm: 96,
    length: "4:00",
  },
  {
    title: "Scott Holmes - Corporate Uplifting",
    url: "https://files.freemusicarchive.org/storage-freemusicarchive-org/music/no_curator/Scott_Holmes_Music/Corporate__Motivational_Music/Scott_Holmes_Music_-_Corporate_Uplifting.mp3",
    category: "Moments of Joy",
    style: "Corporate",
    mood: "Uplifting",
    bpm: 123,
    length: "2:37",
  },
  {
    title: "Scott Holmes - Inspiring Cinematic Ambient",
    url: "https://files.freemusicarchive.org/storage-freemusicarchive-org/music/no_curator/Scott_Holmes_Music/Documentary__TV_Series/Scott_Holmes_Music_-_Inspiring_Cinematic_Ambient.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Soaring",
    bpm: 72,
    length: "3:25",
  },
  {
    title: "AudioCoffee - Inspiring Speech",
    url: "https://cdn.pixabay.com/audio/2022/03/15/audio_8c5f6c3f7e.mp3",
    category: "Celebration",
    style: "Corporate",
    mood: "Triumphant",
    bpm: 120,
    length: "3:15",
  },
  {
    title: "Lesfm - Inspiring Cinematic Ambient",
    url: "https://cdn.pixabay.com/audio/2022/03/01/audio_4fd889c9d6.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Reverent",
    bpm: 64,
    length: "2:50",
  },
  {
    title: "Olexy - Good Night",
    url: "https://cdn.pixabay.com/audio/2022/02/23/audio_2c20cb94c5.mp3",
    category: "Prayerful",
    style: "Piano",
    mood: "Peaceful",
    bpm: 75,
    length: "3:32",
  },
  {
    title: "Alex-Productions - Hope",
    url: "https://cdn.pixabay.com/audio/2022/03/08/audio_e716a7a3d9.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Hopeful",
    bpm: 78,
    length: "4:11",
  },
  {
    title: "AlexiAction - Inspiring Piano",
    url: "https://cdn.pixabay.com/audio/2022/05/02/audio_d5c0d5f0b2.mp3",
    category: "Prayerful",
    style: "Piano",
    mood: "Reflective",
    bpm: 82,
    length: "2:46",
  },
  {
    title: "FSM Team - Praise",
    url: "https://cdn.pixabay.com/audio/2022/10/03/audio_9bfc1c28d7.mp3",
    category: "Celebration",
    style: "Pop",
    mood: "Joyful",
    bpm: 118,
    length: "2:40",
  },
  {
    title: "SoulProdMusic - Gentle Inspiring Piano",
    url: "https://cdn.pixabay.com/audio/2022/03/09/audio_b81895f41b.mp3",
    category: "Prayerful",
    style: "Piano",
    mood: "Tender",
    bpm: 84,
    length: "2:58",
  },
  {
    title: "ZakharValaha - Uplifting Corporate",
    url: "https://cdn.pixabay.com/audio/2021/08/08/audio_04cb0b4f50.mp3",
    category: "Celebration",
    style: "Corporate",
    mood: "Uplifting",
    bpm: 120,
    length: "3:05",
  },
  {
    title: "Wavecont - Ambient Worship Pad",
    url: "https://cdn.pixabay.com/audio/2021/11/25/audio_16a9468457.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Soaking",
    bpm: 65,
    length: "4:20",
  },
  {
    title: "PenguinMusic - Sunday Spirit",
    url: "https://cdn.pixabay.com/audio/2021/09/20/audio_16145c4011.mp3",
    category: "Moments of Joy",
    style: "Pop",
    mood: "Bright",
    bpm: 115,
    length: "2:47",
  },
  {
    title: "Keys of Moon - Inspiring Dream",
    url: "https://cdn.pixabay.com/audio/2022/03/14/audio_f2df80c9b4.mp3",
    category: "Prayerful",
    style: "Orchestral",
    mood: "Hopeful",
    bpm: 92,
    length: "3:10",
  },
  {
    title: "Rozenkreuz - Atmosphere",
    url: "https://cdn.pixabay.com/audio/2022/03/12/audio_f1aeb4c1b3.mp3",
    category: "Prayerful",
    style: "Ambient",
    mood: "Reverent",
    bpm: 70,
    length: "3:45",
  },
];

// Define overlay sounds grouped by category for quick triggering
const overlaySounds = [
  { title: "Joyful Clap Loop", url: "https://cdn.pixabay.com/audio/2022/02/15/audio_6d18f8d63b.mp3", category: "Percussion" },
  { title: "Celestial Shaker", url: "https://cdn.pixabay.com/audio/2022/03/08/audio_aaeb3fb69c.mp3", category: "Percussion" },
  { title: "Deep Kick Pulse", url: "https://cdn.pixabay.com/audio/2022/03/09/audio_a516a4740f.mp3", category: "Percussion" },
  { title: "Snare Accent", url: "https://cdn.pixabay.com/audio/2022/03/10/audio_479ab0ee0b.mp3", category: "Percussion" },
  { title: "Handclap Stack", url: "https://cdn.pixabay.com/audio/2022/02/22/audio_1ddfcacffd.mp3", category: "Percussion" },
  { title: "Crowd Celebration", url: "https://cdn.pixabay.com/audio/2021/09/28/audio_fc3cf49d63.mp3", category: "Crowd" },
  { title: "Amen Chant", url: "https://cdn.pixabay.com/audio/2023/03/19/audio_6dc149048a.mp3", category: "Crowd" },
  { title: "Call & Response", url: "https://cdn.pixabay.com/audio/2023/02/28/audio_08917d427d.mp3", category: "Crowd" },
  { title: "Guitar Accent", url: "https://cdn.pixabay.com/audio/2022/03/10/audio_45f2720028.mp3", category: "Musical Hooks" },
  { title: "Piano Spark", url: "https://cdn.pixabay.com/audio/2021/09/14/audio_d6c5f5f4c1.mp3", category: "Musical Hooks" },
  { title: "Organ Lift", url: "https://cdn.pixabay.com/audio/2022/01/18/audio_8c062a5c6c.mp3", category: "Musical Hooks" },
  { title: "Trumpet Fanfare", url: "https://cdn.pixabay.com/audio/2022/03/29/audio_1a2ac2ff49.mp3", category: "Transitions" },
  { title: "Heavenly Sweep", url: "https://cdn.pixabay.com/audio/2022/03/29/audio_fa3ed4f0d2.mp3", category: "Transitions" },
  { title: "Digital Rise", url: "https://cdn.pixabay.com/audio/2022/10/18/audio_6f6f4dd46e.mp3", category: "Transitions" },
  { title: "Ambient Pad Hit", url: "https://cdn.pixabay.com/audio/2021/08/08/audio_d7da2f90e4.mp3", category: "Atmosphere" },
  { title: "String Swell", url: "https://cdn.pixabay.com/audio/2021/09/06/audio_0d700ddbea.mp3", category: "Atmosphere" },
  { title: "Wind Chime", url: "https://cdn.pixabay.com/audio/2022/01/12/audio_e9cc05463f.mp3", category: "Atmosphere" },
  { title: "Soft Pad Glide", url: "https://cdn.pixabay.com/audio/2022/03/26/audio_44f8285347.mp3", category: "Atmosphere" },
  { title: "Vocal Hit", url: "https://cdn.pixabay.com/audio/2023/01/31/audio_d01b0a173d.mp3", category: "Voices" },
  { title: "Praise Shout", url: "https://cdn.pixabay.com/audio/2023/03/12/audio_a9d2be71db.mp3", category: "Voices" },
  { title: "Choir Pad", url: "https://cdn.pixabay.com/audio/2022/02/09/audio_b74a1a1b6e.mp3", category: "Voices" },
  { title: "Flute Melody", url: "https://cdn.pixabay.com/audio/2022/03/16/audio_2e076ef9f5.mp3", category: "Melodic" },
  { title: "Synth Arp Spark", url: "https://cdn.pixabay.com/audio/2022/03/14/audio_264996843f.mp3", category: "Melodic" },
  { title: "Futuristic Blip", url: "https://cdn.pixabay.com/audio/2022/02/15/audio_0365ef97bd.mp3", category: "Textures" },
  { title: "Electronic Pulse", url: "https://cdn.pixabay.com/audio/2022/03/14/audio_ae6b40c880.mp3", category: "Textures" },
  { title: "Space Whistle", url: "https://cdn.pixabay.com/audio/2022/03/14/audio_9dc32d35a8.mp3", category: "Textures" },
  { title: "Chill Vibe Stab", url: "https://cdn.pixabay.com/audio/2021/10/18/audio_a5e2422e23.mp3", category: "Textures" },
];

const stylePrograms = [
  { code: "001", label: "001 ~ 8 BEAT" },
  { code: "016", label: "016 ~ 16 BEAT" },
  { code: "025", label: "025 ~ BALLAD" },
  { code: "037", label: "037 ~ DANCE" },
  { code: "052", label: "052 ~ DISCO" },
  { code: "059", label: "059 ~ SWING & JAZZ" },
  { code: "076", label: "076 ~ R&B" },
  { code: "086", label: "086 ~ COUNTRY" },
  { code: "095", label: "095 ~ LATIN" },
  { code: "107", label: "107 ~ BALLROOM" },
  { code: "118", label: "118 ~ TRADITIONAL" },
  { code: "129", label: "129 ~ WALTZ" },
  { code: "138", label: "138 ~ CHILDREN" },
  { code: "143", label: "143 ~ PIANIST" },
  { code: "161", label: "161" },
  { code: "FLASH", label: "FLASH MEMORY" },
];

const styleChordMap = {
  DEFAULT: ["C3", "E3", "G3"],
  "001": ["C3", "E3", "G3"],
  "016": ["D3", "F3", "A3"],
  "025": ["A2", "C3", "E3"],
  "037": ["F3", "A3", "C4"],
  "052": ["G3", "B3", "D4"],
  "059": ["Bb2", "D3", "F3"],
  "076": ["E3", "G3", "B3"],
  "086": ["D3", "G3", "B3"],
  "095": ["A3", "C4", "E4"],
  "107": ["C3", "G3", "A3"],
  "118": ["G2", "C3", "E3"],
  "129": ["C3", "E3", "G3"],
  "138": ["F3", "G3", "C4"],
  "143": ["E3", "G3", "C4"],
  "161": ["Bb2", "D3", "F3"],
  FLASH: ["G3", "Bb3", "D4"],
};

const voicePrograms = [
  {
    code: "001",
    label: "001 ~ PIANO",
    settings: { oscillator: { type: "amsine" }, envelope: { attack: 0.01, decay: 0.3, sustain: 0.4, release: 1.4 } },
  },
  {
    code: "007",
    label: "007 ~ E. PIANO",
    settings: { oscillator: { type: "triangle" }, envelope: { attack: 0.02, decay: 0.2, sustain: 0.5, release: 1 } },
  },
  {
    code: "013",
    label: "013 ~ ORGAN",
    settings: { oscillator: { type: "square" }, envelope: { attack: 0.01, decay: 0.1, sustain: 0.8, release: 0.5 } },
  },
  {
    code: "027",
    label: "027 ~ ACCORDION",
    settings: { oscillator: { type: "amtriangle" }, envelope: { attack: 0.05, decay: 0.2, sustain: 0.6, release: 1 } },
  },
  {
    code: "032",
    label: "032 ~ GUITAR",
    settings: { oscillator: { type: "fmsquare" }, envelope: { attack: 0.01, decay: 0.2, sustain: 0.3, release: 0.8 } },
  },
  {
    code: "042",
    label: "042 ~ BASS",
    settings: { oscillator: { type: "fmsawtooth" }, envelope: { attack: 0.01, decay: 0.3, sustain: 0.4, release: 0.6 } },
  },
  {
    code: "050",
    label: "050 ~ STRINGS",
    settings: { oscillator: { type: "fmsine" }, envelope: { attack: 0.1, decay: 0.2, sustain: 0.7, release: 1.5 } },
  },
  {
    code: "062",
    label: "062 ~ CHOIR",
    settings: { oscillator: { type: "sine" }, envelope: { attack: 0.2, decay: 0.1, sustain: 0.8, release: 1.8 } },
  },
  {
    code: "066",
    label: "066 ~ SAXOPHONE",
    settings: { oscillator: { type: "triangle" }, envelope: { attack: 0.05, decay: 0.2, sustain: 0.7, release: 1.1 } },
  },
  {
    code: "077",
    label: "077 ~ TRUMPET",
    settings: { oscillator: { type: "sawtooth" }, envelope: { attack: 0.03, decay: 0.2, sustain: 0.5, release: 1 } },
  },
  {
    code: "084",
    label: "084 ~ BRASS",
    settings: { oscillator: { type: "square" }, envelope: { attack: 0.04, decay: 0.25, sustain: 0.6, release: 1.2 } },
  },
  {
    code: "090",
    label: "090 ~ FLUTE",
    settings: { oscillator: { type: "sine" }, envelope: { attack: 0.08, decay: 0.1, sustain: 0.8, release: 1.4 } },
  },
  {
    code: "097",
    label: "097 ~ SYNTH LEAD",
    settings: { oscillator: { type: "sawtooth" }, envelope: { attack: 0.02, decay: 0.2, sustain: 0.5, release: 0.9 } },
  },
  {
    code: "104",
    label: "104 ~ SYNTH PAD",
    settings: { oscillator: { type: "triangle" }, envelope: { attack: 0.15, decay: 0.2, sustain: 0.7, release: 1.8 } },
  },
  {
    code: "109",
    label: "109 ~ PERCUSSION",
    settings: { oscillator: { type: "square" }, envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.2 } },
  },
  {
    code: "117",
    label: "117 ~ DRUM KITS",
    settings: { oscillator: { type: "square" }, envelope: { attack: 0.001, decay: 0.15, sustain: 0, release: 0.2 } },
  },
  {
    code: "129",
    label: "129~489 XGlite",
    settings: { oscillator: { type: "amsquare" }, envelope: { attack: 0.03, decay: 0.2, sustain: 0.5, release: 1 } },
  },
];

const pianoKeys = ["C3", "D3", "E3", "F3", "G3", "A3", "B3", "C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5"];

// Sample options for custom chords and melody
const sampleChords = [
  "C4,E4,G4; G3,B3,D4",
  "Am,C,F; Dm,F,A",
  "F4,A4,C5; G4,B4,D5"
];
const sampleMelodies = [
  "E4,G4,A4,G4,E4,D4,C4",
  "C4,D4,E4,F4,G4",
  "A3,C4,E4,C4,A3"
];

const worshipScenes = [
  {
    name: "Gentle Prayer",
    tempo: 72,
    reverb: 0.6,
    attack: 0.2,
    release: 2.5,
    chords: sampleChords[0],
    melody: sampleMelodies[1],
    instruments: { drums: false, flute: true, violin: true, guitar: false },
  },
  {
    name: "Joyful Praise",
    tempo: 110,
    reverb: 0.35,
    attack: 0.05,
    release: 1.2,
    chords: sampleChords[1],
    melody: sampleMelodies[0],
    instruments: { drums: true, flute: false, violin: false, guitar: true },
  },
  {
    name: "Soaking Atmosphere",
    tempo: 80,
    reverb: 0.8,
    attack: 0.4,
    release: 3,
    chords: sampleChords[2],
    melody: sampleMelodies[2],
    instruments: { drums: false, flute: true, violin: true, guitar: true },
  },
];

const loopDeckTemplates = [
  {
    id: "kick",
    label: "Kick Grid",
    description: "Low-end pulse that anchors the groove.",
    synthType: "MembraneSynth",
    pattern: [
      { notes: ["C2"], duration: "8n", offset: "0:0:0" },
      { notes: ["C2"], duration: "16n", offset: "0:0:2" },
    ],
    interval: "1m",
    volume: -8,
  },
  {
    id: "bass",
    label: "Sub Bass",
    description: "Warm bass notes to fill the mix.",
    synthType: "AMSynth",
    synthSettings: {
      oscillator: { type: "fmsawtooth" },
      envelope: { attack: 0.01, decay: 0.2, sustain: 0.6, release: 0.8 },
    },
    pattern: [
      { notes: ["C3", "G2"], duration: "4n", offset: "0:0:0" },
      { notes: ["E3"], duration: "4n", offset: "0:1:0" },
    ],
    interval: "1m",
    volume: -6,
  },
  {
    id: "pad",
    label: "Ambient Pad",
    description: "Sweeping harmonies to lift the atmosphere.",
    synthType: "FMSynth",
    synthSettings: {
      oscillator: { type: "sine" },
      envelope: { attack: 0.2, decay: 0.3, sustain: 0.7, release: 2 },
    },
    pattern: [{ notes: ["C4"], duration: "2m", offset: "0:0:0" }],
    interval: "2m",
    volume: -12,
  },
];

const fxPresets = [
  {
    label: "Subtle Air",
    filterFreq: 1400,
    filterQ: 1.1,
    delayTime: 0.3,
    delayFeedback: 0.2,
    fxSend: 0.8,
  },
  {
    label: "Cinematic Sweep",
    filterFreq: 900,
    filterQ: 1.8,
    delayTime: 0.45,
    delayFeedback: 0.35,
    fxSend: 0.95,
  },
  {
    label: "Dream Wash",
    filterFreq: 1100,
    filterQ: 0.9,
    delayTime: 0.6,
    delayFeedback: 0.45,
    fxSend: 0.75,
  },
];

const Music = () => {
  // State for search filter
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedMood, setSelectedMood] = useState("All");
  const [sortOption, setSortOption] = useState("title");
  const [lastOverlaySoundId, setLastOverlaySoundId] = useState(null);
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [playlistLoop, setPlaylistLoop] = useState(false);
  const [soloMode, setSoloMode] = useState(true);
  const [playlistVolume, setPlaylistVolume] = useState(DEFAULT_SESSION_VOLUME);
  const [playlistTileSize, setPlaylistTileSize] = useState(200);
  const [lastSelectedVideoId, setLastSelectedVideoId] = useState(null);
  const [sessionStates, setSessionStates] = useState({});
  const [mountedPlayerIds, setMountedPlayerIds] = useState({});
  const [playlistQueue, setPlaylistQueue] = useState([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(PLAYLIST_ID);
  const [selectedStyle, setSelectedStyle] = useState(stylePrograms[0].code);
  const [selectedVoice, setSelectedVoice] = useState(voicePrograms[0].code);
  const [pianoTempo, setPianoTempo] = useState(90);
  const [accompanimentEnabled, setAccompanimentEnabled] = useState(true);
  const [sustainEnabled, setSustainEnabled] = useState(false);
  const [isPianoLooping, setIsPianoLooping] = useState(false);

  // Panel visibility states

  // Background music state and ref
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [isBgPlaying, setIsBgPlaying] = useState(false);
  const bgTempo = 1.0; // playbackRate (1 = normal)
  const [loopBackground, setLoopBackground] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [randomPlayNext, setRandomPlayNext] = useState(false);
  const [loopSetlist, setLoopSetlist] = useState(true);
  const [crossfadeDuration, setCrossfadeDuration] = useState(4);
  const bgAudioRef = useRef(null);
  const bgEndHandlerRef = useRef(null);

  // Setlist state
  const [setlist, setSetlist] = useState([]);
  const [currentSetlistIndex, setCurrentSetlistIndex] = useState(-1);

  // Overlay filter state
  const [overlayCategory, setOverlayCategory] = useState("All");

  // Mixer controls state
  const [tempo, setTempo] = useState(100);
  const volume = 0;
  const [customChords, setCustomChords] = useState("");
  const [customMelody, setCustomMelody] = useState("");
  const [instruments, setInstruments] = useState({
    drums: false,
    flute: false,
    violin: false,
    guitar: false,
  });
  const [reverbAmount, setReverbAmount] = useState(0.3);
  const [attack, setAttack] = useState(0.05);
  const [release, setRelease] = useState(1);
  const [loopDeckState, setLoopDeckState] = useState({});
  const [loopDeckBpm, setLoopDeckBpm] = useState(100);
  const [filterFreq, setFilterFreq] = useState(1600);
  const [filterQ, setFilterQ] = useState(1);
  const [delayTime, setDelayTime] = useState(0.35);
  const [delayFeedback, setDelayFeedback] = useState(0.4);
  const [fxSend, setFxSend] = useState(0.85);

  // Refs for Tone.js overlay synths
  const melodySynthRef = useRef(null);
  const chordSynthRef = useRef(null);
  const reverbRef = useRef(null);
  const instrumentSynthsRef = useRef({});
  const playerRefs = useRef({});
  const pendingSessionActions = useRef({});
  const pianoSynthRef = useRef(null);
  const overlayAudioRef = useRef(null);
  const pianoIntervalRef = useRef(null);
  const loopRefs = useRef({});
  const loopSynthRefs = useRef({});
  const fxChainRef = useRef(null);
  const [controlsOpen, setControlsOpen] = useState(false);

  // Helpers and derived collections
  const currentPlaylistInfo =
    playlistsMeta.find((pl) => pl.id === selectedPlaylistId) ||
    playlistsMeta.find((pl) => pl.id === PLAYLIST_ID) ||
    playlistsMeta[0] ||
    {};
  const playlistOptions = playlistsMeta.map((pl) => ({
    id: pl.id,
    title: pl.title,
  }));
  const getVideoId = (video) =>
    (video && (video.videoId || video.id || video?.contentDetails?.videoId)) || "";
  const playlistVideos = useMemo(() => {
    const rawList = Array.isArray(playlistItemsData[selectedPlaylistId])
      ? playlistItemsData[selectedPlaylistId]
      : [];
    return rawList
      .map((video) => {
        const videoId = getVideoId(video);
        if (!videoId) return null;
        return {
          videoId,
          title: video.title || "Untitled",
          thumbnails: video.thumbnails,
        };
      })
      .filter(Boolean);
  }, [selectedPlaylistId]);
  const getPlaylistVideoMeta = (id) =>
    playlistVideos.find((video) => getVideoId(video) === id);
  const computeVolume = (db) => {
    const normalized = Math.min(Math.max((db + 60) / 60, 0), 1);
    return normalized * 0.4;
  };

  const safePlayerCall = (videoId, player, method, ...args) => {
    if (!player || typeof player[method] !== "function") return false;
    try {
      player[method](...args);
    } catch (error) {
      console.error(`YouTube player ${method} error (${videoId})`, error);
    }
    return true;
  };

  const fadeAudio = (audio, startVolume, endVolume, durationSeconds, onComplete) => {
    if (!audio) return;
    if (durationSeconds <= 0) {
      audio.volume = endVolume;
      if (onComplete) onComplete();
      return;
    }
    const steps = Math.max(1, Math.round(durationSeconds * 20));
    const stepTime = (durationSeconds * 1000) / steps;
    const volumeStep = (endVolume - startVolume) / steps;
    let currentStep = 0;
    audio.volume = startVolume;
    const interval = setInterval(() => {
      currentStep += 1;
      const nextVolume = startVolume + volumeStep * currentStep;
      audio.volume = Math.min(Math.max(nextVolume, 0), 1);
      if (currentStep >= steps) {
        clearInterval(interval);
        audio.volume = Math.min(Math.max(endVolume, 0), 1);
        if (onComplete) onComplete();
      }
    }, stepTime);
  };

  const transposeNote = (note, semitones = 0) => {
    try {
      return Tone.Frequency(note).transpose(semitones).toNote();
    } catch (err) {
      return note;
    }
  };

  const availableCategories = [
    "All",
    ...Array.from(new Set(musicLibrary.map((track) => track.category))).sort(),
  ];
  const availableMoods = [
    "All",
    ...Array.from(new Set(musicLibrary.map((track) => track.mood))).sort(),
  ];
  const overlayCategories = [
    "All",
    ...Array.from(new Set(overlaySounds.map((sound) => sound.category))).sort(),
  ];
  const activeLoopCount = loopDeckTemplates.filter((loop) => loopDeckState[loop.id]).length;
  const normalizedPlaylistSearch = playlistSearch.trim().toLowerCase();
  const playlistTiles = useMemo(() => playlistVideos.map((video) => {
    if (!normalizedPlaylistSearch) {
      return { video, videoId: video.videoId, matchesSearch: true };
    }
    const haystack = `${video.title ?? ""}`.toLowerCase();
    return {
      video,
      videoId: video.videoId,
      matchesSearch: haystack.includes(normalizedPlaylistSearch),
    };
  }), [playlistVideos, normalizedPlaylistSearch]);
  const visiblePlaylistCount = playlistTiles.filter((entry) => entry.matchesSearch).length;
  const isPlaylistQueueEmpty = playlistQueue.length === 0;
  const activePlaylistTitles = Object.entries(sessionStates)
    .filter(([, state]) => state.status === "playing")
    .map(([id]) => getPlaylistVideoMeta(id)?.title || id)
    .filter(Boolean);
  const getVoiceSettings = (voiceCode) => {
    const voice = voicePrograms.find((entry) => entry.code === voiceCode);
    return (
      voice?.settings || { oscillator: { type: "sine" }, envelope: { attack: 0.02, decay: 0.3, sustain: 0.5, release: 1.2 } }
    );
  };

  const ensurePianoSynth = async () => {
    await Tone.start();
    if (!pianoSynthRef.current) {
      pianoSynthRef.current = new Tone.PolySynth(Tone.Synth, getVoiceSettings(selectedVoice)).toDestination();
    }
    return pianoSynthRef.current;
  };

  const getStyleChord = (styleCode) => styleChordMap[styleCode] || styleChordMap.DEFAULT;

  const stopPianoLoop = ({ keepState = false } = {}) => {
    if (pianoIntervalRef.current) {
      clearInterval(pianoIntervalRef.current);
      pianoIntervalRef.current = null;
    }
    if (pianoSynthRef.current) {
      pianoSynthRef.current.releaseAll();
    }
    if (!keepState) {
      setIsPianoLooping(false);
    }
  };

  const startPianoLoop = async ({ restart = false } = {}) => {
    const synth = await ensurePianoSynth();
    stopPianoLoop({ keepState: true });
    const baseChord = getStyleChord(selectedStyle);
    const highChord = baseChord.map((note) => transposeNote(note, 12));
    const intervalMs = Math.max(400, Math.round((60 / pianoTempo) * 1000));
    const duration = sustainEnabled ? 1.2 : 0.6;

    const playPattern = () => {
      synth.triggerAttackRelease(baseChord, duration);
      if (accompanimentEnabled) {
        synth.triggerAttackRelease(highChord, duration * 0.8);
        synth.triggerAttackRelease([baseChord[0]], duration * 0.5);
      }
    };

    playPattern();
    pianoIntervalRef.current = setInterval(playPattern, intervalMs * 2);
    if (!restart) {
      setIsPianoLooping(true);
    }
  };
  const handlePianoKeyPress = async (note) => {
    const synth = await ensurePianoSynth();
    const tempoClamped = Math.min(Math.max(pianoTempo, 30), 200);
    const beatSeconds = 60 / tempoClamped;
    const duration = sustainEnabled ? beatSeconds * 2 : beatSeconds;
    synth.triggerAttackRelease(note, duration);
    if (accompanimentEnabled) {
      const chord = getStyleChord(selectedStyle);
      synth.triggerAttackRelease(chord, duration * 1.2);
    }
  };

  const handleStopPiano = () => {
    stopPianoLoop();
  };

  const updateSessionState = (videoId, updates) => {
    setSessionStates((prev) => ({
      ...prev,
      [videoId]: {
        status: "idle",
        ...prev[videoId],
        ...updates,
      },
    }));
  };

  const pauseOtherSessions = (activeId) => {
    Object.entries(playerRefs.current).forEach(([id, player]) => {
      if (id !== activeId) {
        safePlayerCall(id, player, "pauseVideo");
      }
    });
    setSessionStates((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (id !== activeId) {
          next[id] = { ...next[id], status: "paused" };
        }
      });
      return next;
    });
  };

  const handleSessionPlay = (videoId, { respectSolo = true } = {}) => {
    if (respectSolo && soloMode) {
      pauseOtherSessions(videoId);
    }
    const player = playerRefs.current[videoId];
    setLastSelectedVideoId(videoId);
    if (safePlayerCall(videoId, player, "playVideo")) {
      updateSessionState(videoId, { status: "playing" });
    } else {
      pendingSessionActions.current[videoId] = "play";
      updateSessionState(videoId, { status: "loading" });
    }
  };

  const handleSessionPause = (videoId) => {
    const player = playerRefs.current[videoId];
    safePlayerCall(videoId, player, "pauseVideo");
    updateSessionState(videoId, { status: "paused" });
  };

  const ensurePlayerMounted = (videoId) => {
    setMountedPlayerIds((prev) => (prev[videoId] ? prev : { ...prev, [videoId]: true }));
  };

  const handlePlaylistTileToggle = (videoId) => {
    setLastSelectedVideoId(videoId);
    ensurePlayerMounted(videoId);
    const status = sessionStates[videoId]?.status;
    if (status === "playing") {
      handleSessionPause(videoId);
    } else {
      handleSessionPlay(videoId);
    }
  };

  const handleGlobalVolumeChange = (value) => {
    setPlaylistVolume(value);
    Object.values(playerRefs.current).forEach((player) => {
      safePlayerCall("global", player, "setVolume", value);
    });
  };

  const handleAddSelectedToQueue = () => {
    if (!lastSelectedVideoId) return;
    const video = playlistVideos.find((item) => getVideoId(item) === lastSelectedVideoId);
    if (!video) return;
    setPlaylistQueue((prev) => {
      if (prev.some((entry) => getVideoId(entry) === getVideoId(video))) return prev;
      return [...prev, video];
    });
  };

  const handleRemoveFromQueue = (videoId) => {
    setPlaylistQueue((prev) => prev.filter((video) => getVideoId(video) !== videoId));
  };

  const handlePlayerReady = (videoId, event) => {
    playerRefs.current[videoId] = event.target;
    safePlayerCall(videoId, event.target, "setVolume", playlistVolume);
    if (pendingSessionActions.current[videoId] === "play") {
      safePlayerCall(videoId, event.target, "playVideo");
      delete pendingSessionActions.current[videoId];
    }
  };

  const handlePlayerStateChange = (videoId, event) => {
    if (event?.data === 0 && playlistLoop) {
      const player = playerRefs.current[videoId];
      if (safePlayerCall(videoId, player, "seekTo", 0, true)) {
        safePlayerCall(videoId, player, "playVideo");
        updateSessionState(videoId, { status: "playing" });
        return;
      }
    }
    const stateMap = {
      "-1": "idle",
      0: "ended",
      1: "playing",
      2: "paused",
      3: "buffering",
    };
    const nextStatus = stateMap[event?.data];
    if (nextStatus) {
      updateSessionState(videoId, { status: nextStatus });
    }
  };

  useEffect(() => {
    if (bgAudioRef.current) {
      bgAudioRef.current.loop = loopBackground;
    }
  }, [loopBackground]);

  useEffect(() => {
    if (reverbRef.current) {
      reverbRef.current.wet.value = reverbAmount;
    }
  }, [reverbAmount]);

  useEffect(() => {
    Tone.Transport.bpm.value = loopDeckBpm;
  }, [loopDeckBpm]);

  useEffect(() => {
    const chain = fxChainRef.current;
    if (chain?.filter) {
      chain.filter.frequency.value = filterFreq;
    }
  }, [filterFreq]);

  useEffect(() => {
    const chain = fxChainRef.current;
    if (chain?.filter) {
      chain.filter.Q.value = filterQ;
    }
  }, [filterQ]);

  useEffect(() => {
    const chain = fxChainRef.current;
    if (chain?.delay) {
      chain.delay.delayTime.value = delayTime;
    }
  }, [delayTime]);

  useEffect(() => {
    const chain = fxChainRef.current;
    if (chain?.delay) {
      chain.delay.feedback.value = delayFeedback;
    }
  }, [delayFeedback]);

  useEffect(() => {
    const chain = fxChainRef.current;
    if (chain?.masterGain) {
      chain.masterGain.gain.value = fxSend;
    }
  }, [fxSend]);

  useEffect(() => {
    return () => {
      stopLoopDeck();
      if (fxChainRef.current) {
        Object.values(fxChainRef.current).forEach((node) => {
          if (node && typeof node.dispose === "function") {
            node.dispose();
          }
        });
        fxChainRef.current = null;
      }
    };
  }, []);


  useEffect(() => {
    const initial = {};
    playlistVideos.forEach((video) => {
      const id = getVideoId(video);
      if (id) initial[id] = { status: "idle" };
    });
    setSessionStates(initial);
    setLastSelectedVideoId(null);
    setPlaylistQueue([]);
  }, [selectedPlaylistId, playlistVideos]);

  useEffect(() => {
    const next = {};
    playlistVideos.forEach((video) => {
      const id = getVideoId(video);
      if (id) next[id] = true;
    });
    setMountedPlayerIds(next);
  }, [playlistVideos]);

  useEffect(() => {
    const validIds = new Set(playlistVideos.map((video) => getVideoId(video)).filter(Boolean));
    Object.entries(playerRefs.current).forEach(([id, player]) => {
      if (!validIds.has(id)) {
        safePlayerCall(id, player, "stopVideo");
        delete playerRefs.current[id];
        delete pendingSessionActions.current[id];
      }
    });
  }, [playlistVideos]);

  useEffect(() => {
    if (pianoSynthRef.current) {
      pianoSynthRef.current.dispose();
      pianoSynthRef.current = null;
    }
  }, [selectedVoice]);

  useEffect(() => {
    if (!lastOverlaySoundId) return undefined;
    const timeout = setTimeout(() => setLastOverlaySoundId(null), 2000);
    return () => clearTimeout(timeout);
  }, [lastOverlaySoundId]);

  useEffect(() => {
    if (isPianoLooping) {
      startPianoLoop({ restart: true });
    }
  }, [selectedStyle, selectedVoice, pianoTempo, accompanimentEnabled, sustainEnabled]);

  useEffect(() => {
    return () => {
      stopPianoLoop({ keepState: true });
      if (pianoSynthRef.current) {
        pianoSynthRef.current.dispose();
        pianoSynthRef.current = null;
      }
      Object.entries(playerRefs.current).forEach(([id, player]) => {
        safePlayerCall(id, player, "stopVideo");
      });
      playerRefs.current = {};
      pendingSessionActions.current = {};
      if (overlayAudioRef.current) {
        overlayAudioRef.current.pause();
        overlayAudioRef.current = null;
      }
    };
  }, []);

  // Filter music library based on search term and category selections
  const filteredTracks = musicLibrary.filter((track) => {
    const matchesSearch = track.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || track.category === selectedCategory;
    const matchesMood = selectedMood === "All" || track.mood === selectedMood;
    return matchesSearch && matchesCategory && matchesMood;
  });

  const sortedTracks = [...filteredTracks].sort((a, b) => {
    if (sortOption === "bpmAsc") {
      return a.bpm - b.bpm;
    }
    if (sortOption === "bpmDesc") {
      return b.bpm - a.bpm;
    }
    if (sortOption === "mood") {
      return a.mood.localeCompare(b.mood);
    }
    return a.title.localeCompare(b.title);
  });

  function playTrack(track, options = {}) {
    if (!track) return;
    const fromSetlistIndex =
      typeof options.fromSetlistIndex === "number"
        ? options.fromSetlistIndex
        : setlist.findIndex((item) => item.title === track.title);

    const previousAudio = bgAudioRef.current;
    const audio = new Audio(track.url);
    audio.loop = loopBackground;
    audio.playbackRate = bgTempo;
    audio.volume = 0;

    if (bgEndHandlerRef.current && previousAudio) {
      previousAudio.removeEventListener("ended", bgEndHandlerRef.current);
      bgEndHandlerRef.current = null;
    }

    const handleEnded = () => {
      setIsBgPlaying(false);
      if (autoAdvance) {
        playNextInSetlist();
      }
    };

    audio.addEventListener("ended", handleEnded);
    bgEndHandlerRef.current = handleEnded;
    bgAudioRef.current = audio;

    if (previousAudio) {
      const previousVolume = previousAudio.volume;
      fadeAudio(previousAudio, previousVolume, 0, crossfadeDuration, () => {
        previousAudio.pause();
      });
    }

    audio
      .play()
      .then(() => {
        fadeAudio(audio, 0, computeVolume(volume), crossfadeDuration);
        setSelectedTrack(track);
        setIsBgPlaying(true);
        setCurrentSetlistIndex(fromSetlistIndex);
      })
      .catch((err) => console.error("Error playing track:", err));
  }

  function stopTrack({ immediate = false } = {}) {
    const current = bgAudioRef.current;
    if (current) {
      const finish = () => {
        current.pause();
        if (bgEndHandlerRef.current) {
          current.removeEventListener("ended", bgEndHandlerRef.current);
          bgEndHandlerRef.current = null;
        }
      };
      if (immediate) {
        finish();
      } else {
        fadeAudio(current, current.volume, 0, crossfadeDuration, finish);
      }
      bgAudioRef.current = null;
    }
    setIsBgPlaying(false);
  }

  function playNextInSetlist(step = 1) {
    if (setlist.length === 0) return;
    let nextIndex = currentSetlistIndex;

    if (randomPlayNext && step === 1 && setlist.length > 1) {
      const excludeIndex = nextIndex < 0 ? -1 : nextIndex;
      let candidate = excludeIndex;
      let guard = 0;
      while (candidate === excludeIndex && guard < 8) {
        candidate = Math.floor(Math.random() * setlist.length);
        guard += 1;
      }
      if (candidate === excludeIndex) {
        candidate = excludeIndex >= 0 ? (excludeIndex + 1) % setlist.length : 0;
      }
      nextIndex = candidate;
    } else {
      if (nextIndex === -1) {
        nextIndex = 0;
      } else {
        nextIndex += step;
      }

      if (nextIndex >= setlist.length) {
        if (loopSetlist) {
          nextIndex = 0;
        } else {
          stopTrack();
          return;
        }
      }

      if (nextIndex < 0) {
        if (loopSetlist) {
          nextIndex = setlist.length - 1;
        } else {
          nextIndex = 0;
        }
      }
    }

    const nextTrack = setlist[nextIndex];
    if (nextTrack) {
      playTrack(nextTrack, { fromSetlistIndex: nextIndex });
    }
  }

  function playPreviousInSetlist() {
    playNextInSetlist(-1);
  }

  const addToSetlist = (track) => {
    if (!track) return;
    setSetlist((prev) => {
      if (prev.some((item) => item.title === track.title)) {
        return prev;
      }
      return [...prev, track];
    });
  };

  const removeFromSetlist = (index) => {
    setSetlist((prev) => prev.filter((_, idx) => idx !== index));
    setCurrentSetlistIndex((prev) => {
      if (prev === index) return -1;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const moveSetlistItem = (index, direction) => {
    setSetlist((prev) => {
      const newList = [...prev];
      const target = index + direction;
      if (target < 0 || target >= newList.length) return prev;
      const [item] = newList.splice(index, 1);
      newList.splice(target, 0, item);
      return newList;
    });
    setCurrentSetlistIndex((prev) => {
      if (prev === index) return index + direction;
      if (prev === index + direction) return index;
      return prev;
    });
  };

  const clearSetlist = () => {
    setSetlist([]);
    setCurrentSetlistIndex(-1);
  };

  const startSetlist = () => {
    if (setlist.length > 0) {
      playTrack(setlist[0], { fromSetlistIndex: 0 });
    }
  };

  // Play a ready-made overlay sound (one-shot)
  const playOverlaySound = (sound) => {
    if (!sound || !sound.url) return;
    if (overlayAudioRef.current) {
      overlayAudioRef.current.pause();
      overlayAudioRef.current = null;
    }
    const overlayAudio = new Audio();
    overlayAudio.crossOrigin = "anonymous";
    overlayAudio.src = sound.url;
    overlayAudio.volume = 0.9;
    overlayAudioRef.current = overlayAudio;
    setLastOverlaySoundId(sound.title);
    overlayAudio.play().catch((err) => console.error("Overlay sound error:", err));
  };

  // Start custom overlay mix using Tone.js (for chords/melody)
  const startMixing = async () => {
    await Tone.start();

    stopLoopDeck();

    Tone.Transport.stop();
    Tone.Transport.cancel();

    if (melodySynthRef.current) {
      melodySynthRef.current.dispose();
      melodySynthRef.current = null;
    }
    if (chordSynthRef.current) {
      chordSynthRef.current.dispose();
      chordSynthRef.current = null;
    }
    Object.values(instrumentSynthsRef.current).forEach((synth) => synth.dispose());
    instrumentSynthsRef.current = {};
    if (reverbRef.current) {
      reverbRef.current.dispose();
    }
    reverbRef.current = new Tone.Reverb({ decay: 4, wet: reverbAmount }).toDestination();

    const destination = reverbRef.current;

    melodySynthRef.current = new Tone.Synth({
      oscillator: { type: "sine" },
      envelope: { attack, release },
    }).connect(destination);

    chordSynthRef.current = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: "triangle" },
      envelope: { attack, release },
    }).connect(destination);

    Tone.Transport.bpm.value = tempo;
    const chords = customChords.trim()
      ? customChords.split(";").map((str) => ({
          notes: str.split(",").map((n) => n.trim()),
          duration: "2n",
        }))
      : [];
    const melody = customMelody.trim()
      ? customMelody.split(",").map((n) => n.trim())
      : [];

    if (chords.length > 0) {
      let chordIndex = 0;
      Tone.Transport.scheduleRepeat((time) => {
        const chord = chords[chordIndex % chords.length];
        chordSynthRef.current.triggerAttackRelease(chord.notes, chord.duration, time);
        chordIndex++;
      }, "2n");
    }

    if (melody.length > 0) {
      let melodyIndex = 0;
      Tone.Transport.scheduleRepeat((time) => {
        const note = melody[melodyIndex % melody.length];
        melodySynthRef.current.triggerAttackRelease(note, "8n", time);
        melodyIndex++;
      }, "4n");
    }

    if (instruments.drums) {
      const drums = new Tone.MembraneSynth({ volume: -8 }).connect(destination);
      instrumentSynthsRef.current.drums = drums;
      Tone.Transport.scheduleRepeat((time) => {
        drums.triggerAttackRelease("C2", "8n", time);
        drums.triggerAttackRelease("G2", "16n", time + Tone.Time("0:2:0"));
      }, "1m");
    }

    if (instruments.flute) {
      const flute = new Tone.Synth({
        oscillator: { type: "triangle" },
        envelope: { attack: 0.3, release: 1.5 },
      }).connect(destination);
      instrumentSynthsRef.current.flute = flute;
      Tone.Transport.scheduleRepeat((time) => {
        flute.triggerAttackRelease("A4", "8n", time);
        flute.triggerAttackRelease("C5", "8n", time + Tone.Time("0:1:0"));
      }, "2m", Tone.Time("0:1:0"));
    }

    if (instruments.violin) {
      const violin = new Tone.Synth({
        oscillator: { type: "sawtooth" },
        envelope: { attack: 0.6, release: 2 },
      }).connect(destination);
      instrumentSynthsRef.current.violin = violin;
      Tone.Transport.scheduleRepeat((time) => {
        violin.triggerAttackRelease(["E4", "B4"], "2n", time);
      }, "2m", Tone.Time("0:2:0"));
    }

    if (instruments.guitar) {
      const guitar = new Tone.Synth({
        oscillator: { type: "square" },
        envelope: { attack: 0.02, decay: 0.3, sustain: 0.2, release: 0.8 },
      }).connect(destination);
      instrumentSynthsRef.current.guitar = guitar;
      Tone.Transport.scheduleRepeat((time) => {
        guitar.triggerAttackRelease("C4", "8n", time);
        guitar.triggerAttackRelease("F4", "8n", time + Tone.Time("0:1:2"));
      }, "1m", Tone.Time("0:0:2"));
    }

    Tone.Transport.start();
  };

  const stopMixing = () => {
    Tone.Transport.stop();
    Tone.Transport.cancel();
    if (melodySynthRef.current) {
      melodySynthRef.current.dispose();
      melodySynthRef.current = null;
    }
    if (chordSynthRef.current) {
      chordSynthRef.current.dispose();
      chordSynthRef.current = null;
    }
    Object.values(instrumentSynthsRef.current).forEach((synth) => synth.dispose());
    instrumentSynthsRef.current = {};
    if (reverbRef.current) {
      reverbRef.current.dispose();
      reverbRef.current = null;
    }
  };

  const handleLibraryTileToggle = (track) => {
    if (selectedTrack && selectedTrack.title === track.title && isBgPlaying) {
      stopTrack();
    } else {
      playTrack(track);
    }
  };

  const handleQuickAddToSetlist = (track) => {
    addToSetlist(track);
  };

  const ensureFxChain = () => {
    if (fxChainRef.current) return fxChainRef.current;
    const masterGain = new Tone.Gain(fxSend).toDestination();
    const delay = new Tone.FeedbackDelay(delayTime, delayFeedback).connect(masterGain);
    const filter = new Tone.Filter(filterFreq, "lowpass").connect(delay);
    fxChainRef.current = { filter, delay, masterGain };
    return fxChainRef.current;
  };

  const getLoopTemplate = (loopId) => loopDeckTemplates.find((entry) => entry.id === loopId);

  const startLoopById = (loopId) => {
    if (loopRefs.current[loopId]) return;
    const template = getLoopTemplate(loopId);
    if (!template) return;
    const chain = ensureFxChain();
    const SynthClass = Tone[template.synthType] || Tone.Synth;
    const synth = new SynthClass(template.synthSettings || {}).connect(chain.filter);
    if (typeof template.volume === "number" && synth.volume) {
      synth.volume.value = template.volume;
    }
    const loop = new Tone.Loop((time) => {
      template.pattern.forEach(({ notes, duration, offset = "0" }) => {
        if (!notes) return;
        const noteList = Array.isArray(notes) ? notes : [notes];
        const entryOffset = Tone.Time(offset).toSeconds();
        noteList.forEach((note, noteIndex) => {
          if (!note) return;
          const noteOffsetSeconds = entryOffset + noteIndex * 0.005;
          synth.triggerAttackRelease(note, duration, time + noteOffsetSeconds);
        });
      });
    }, template.interval).start(0);
    loopRefs.current[loopId] = loop;
    loopSynthRefs.current[loopId] = synth;
    if (Tone.Transport.state !== "started") {
      Tone.Transport.start("+0.1");
    }
  };

  const stopLoopById = (loopId) => {
    const loop = loopRefs.current[loopId];
    if (loop) {
      loop.stop();
      loop.dispose();
      delete loopRefs.current[loopId];
    }
    const synth = loopSynthRefs.current[loopId];
    if (synth) {
      if (typeof synth.releaseAll === "function") {
        synth.releaseAll();
      }
      synth.dispose();
      delete loopSynthRefs.current[loopId];
    }
  };

  const stopLoopDeck = () => {
    Object.keys(loopRefs.current).forEach((loopId) => stopLoopById(loopId));
    setLoopDeckState({});
  };

  const handleLoopToggle = async (loopId) => {
    await Tone.start();
    setLoopDeckState((prev) => {
      const isActive = !prev[loopId];
      if (isActive) {
        startLoopById(loopId);
      } else {
        stopLoopById(loopId);
      }
      return { ...prev, [loopId]: isActive };
    });
  };

  const applyFxPreset = (preset) => {
    setFilterFreq(preset.filterFreq);
    setFilterQ(preset.filterQ);
    setDelayTime(preset.delayTime);
    setDelayFeedback(preset.delayFeedback);
    setFxSend(preset.fxSend);
  };

  // Render music library tiles
  const renderTiles = () => {
    return sortedTracks.map((track) => {
      const isActive = selectedTrack && selectedTrack.title === track.title && isBgPlaying;
      const isQueued = setlist.some((item) => item.title === track.title);
      const tileClasses = ["mix-tile", "compact"];
      if (isActive) tileClasses.push("active");
      if (isQueued) tileClasses.push("selected");
      return (
        <div
          key={track.title}
          className={tileClasses.join(" ")}
          role="button"
          tabIndex={0}
          onClick={() => handleLibraryTileToggle(track)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleLibraryTileToggle(track);
            }
          }}
        >
          <div className="tile-header">
            <strong>{track.title}</strong>
            <span>{track.length}</span>
          </div>
          <div className="tile-meta">
            <span>{track.category}</span>
            <span>{track.bpm} BPM</span>
            <span>{track.mood}</span>
          </div>
          <div className="tile-footer">
            <span className="tile-status-text">
              {isActive ? "Playing — tap to pause" : "Tap tile to cue"}
            </span>
            <div className="tile-icons">
              <button
                type="button"
                className="tile-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLibraryTileToggle(track);
                }}
                title={isActive ? "Pause track" : "Play track"}
              >
                {isActive ? "⏸" : "▶︎"}
              </button>
              <button
                type="button"
                className="tile-icon-btn add"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuickAddToSetlist(track);
                }}
                disabled={isQueued}
                title={isQueued ? "Already in setlist" : "Add to setlist"}
              >
                {isQueued ? "✓" : "+"}
              </button>
            </div>
          </div>
        </div>
      );
    });
  };

  // Render overlay sound buttons
  const renderOverlayButtons = () => {
    const filteredOverlays = overlayCategory === "All"
      ? overlaySounds
      : overlaySounds.filter((sound) => sound.category === overlayCategory);
    return filteredOverlays.map((sound) => {
      const isActive = lastOverlaySoundId === sound.title;
      return (
        <div
          key={sound.title}
          className={`overlay-chip ${isActive ? "active" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => playOverlaySound(sound)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              playOverlaySound(sound);
            }
          }}
        >
          <span className="overlay-icon">{isActive ? "⏺" : "▶︎"}</span>
          <div className="overlay-text">
            <strong>{sound.title}</strong>
            <small>{sound.category}</small>
          </div>
        </div>
      );
    });
  };

  const defaultTrack = sortedTracks[0] || musicLibrary[0];
  const isSetlistEmpty = setlist.length === 0;
  const applyScene = (scene) => {
    setTempo(scene.tempo);
    setReverbAmount(scene.reverb);
    setAttack(scene.attack);
    setRelease(scene.release);
    setCustomChords(scene.chords);
    setCustomMelody(scene.melody);
    setInstruments({ ...scene.instruments });
  };
  const handleBackgroundToggle = () => {
    if (isBgPlaying) {
      stopTrack();
    } else if (selectedTrack) {
      playTrack(selectedTrack);
    } else if (defaultTrack) {
      playTrack(defaultTrack);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleKeyToggle = (event) => {
      if (event.key === "Control" && !event.repeat && !event.altKey && !event.metaKey) {
        setControlsOpen((prev) => !prev);
        return;
      }
      if (event.key === "Escape" && controlsOpen) {
        setControlsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyToggle);
    return () => window.removeEventListener("keydown", handleKeyToggle);
  }, [controlsOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const headerLogo = document.querySelector(".header-logo");
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = "pointer";
    const handleHeaderClick = (event) => {
      event.preventDefault();
      setControlsOpen((prev) => !prev);
    };
    headerLogo.addEventListener("click", handleHeaderClick);
    return () => {
      headerLogo.removeEventListener("click", handleHeaderClick);
      headerLogo.style.cursor = previousCursor;
    };
  }, []);

  return (
    <div className="dj-music-page">
      <SEO
        title="DJ Music"
        description="Background worship music and atmospheres for prayer and gatherings."
      />

      <div className={`music-top-controls${controlsOpen ? " open" : ""}`}>
        <div className="music-top-controls-inner">
          <div className="music-top-controls-header">
            <span>Playback controls</span>
            <button
              type="button"
              className="music-top-controls-close"
              onClick={() => setControlsOpen(false)}
              aria-label="Close controls"
            >
              ×
            </button>
          </div>
          <div className="music-top-controls-actions">
            <button
              type="button"
              className={`music-control-button${autoAdvance ? " active" : ""}`}
              onClick={() => setAutoAdvance((prev) => !prev)}
              aria-pressed={autoAdvance}
              aria-label="Toggle auto advance"
            >
              Auto Next
            </button>
            <button
              type="button"
              className={`music-control-button${randomPlayNext ? " active" : ""}`}
              onClick={() => setRandomPlayNext((prev) => !prev)}
              aria-pressed={randomPlayNext}
              aria-label="Toggle random play"
            >
              Random
            </button>
          </div>
          <div className="music-top-controls-grid">
            <div className="control-pill-group">
              <label className="control-pill">
                <input
                  type="checkbox"
                  checked={soloMode}
                  onChange={(e) => setSoloMode(e.target.checked)}
                />
                Solo mode
              </label>
              <label className="control-pill">
                <input
                  type="checkbox"
                  checked={playlistLoop}
                  onChange={(e) => setPlaylistLoop(e.target.checked)}
                />
                Loop playing track
              </label>
            </div>
            <label className="control-volume compact">
              Volume {playlistVolume}%
              <input
                type="range"
                min="0"
                max="100"
                value={playlistVolume}
                onChange={(e) => handleGlobalVolumeChange(Number(e.target.value))}
              />
            </label>
            <label className="control-volume compact">
              Tile size {playlistTileSize}px
              <input
                type="range"
                min="160"
                max="320"
                value={playlistTileSize}
                onChange={(e) => setPlaylistTileSize(Number(e.target.value))}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Playlist Sessions sourced from GCC YouTube playlist */}
      <section className="dj-playlist-section">
        <div className="panel-content">
          <div className="playlist-controls compact-row">
            <input
              type="text"
              placeholder="Search this playlist..."
              value={playlistSearch}
              onChange={(e) => setPlaylistSearch(e.target.value)}
            />
            <label className="control-field">
              <select
                value={selectedPlaylistId}
                onChange={(e) => setSelectedPlaylistId(e.target.value)}
              >
                {playlistOptions.map((pl) => (
                  <option key={pl.id} value={pl.id}>
                    {pl.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {!isPlaylistQueueEmpty && (
            <div className="playlist-queue">
              <strong>Scratch Deck:</strong>
              <div className="queue-chips">
                {playlistQueue.map((video) => {
                  const videoKey = getVideoId(video);
                  return (
                    <span key={videoKey || video.title} className="queue-chip">
                      {video.title}
                      <button type="button" onClick={() => handleRemoveFromQueue(videoKey)}>✕</button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {visiblePlaylistCount === 0 && (
            <p style={{ opacity: 0.7 }}>No playlist songs match “{playlistSearch}”.</p>
          )}
          <div
            className="playlist-grid compact"
            style={{ "--playlist-tile-size": `${playlistTileSize}px` }}
          >
            {playlistTiles.map(({ video, videoId, matchesSearch }, index) => {
              const sessionState = sessionStates[videoId] || { status: "idle" };
              const thumbnail =
                video.thumbnails?.medium?.url ||
                video.thumbnails?.default?.url ||
                currentPlaylistInfo?.thumbnails?.medium?.url;
              const isActive = sessionState.status === "playing";
              const selectedIcon = lastSelectedVideoId === videoId ? "◎" : null;
              const hidden = !matchesSearch;
              const shouldMountPlayer = mountedPlayerIds[videoId];
              return (
                <div
                  key={videoId}
                  className={`session-card micro ${isActive ? "active" : ""} ${selectedIcon ? "selected" : ""} ${
                    hidden ? "hidden" : ""
                  }`}
                  role="button"
                  tabIndex={hidden ? -1 : 0}
                  aria-label={`Toggle ${video.title}`}
                  onClick={() => !hidden && handlePlaylistTileToggle(videoId)}
                  onKeyDown={(event) => {
                    if (hidden) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handlePlaylistTileToggle(videoId);
                    }
                  }}
                >
                  <div className="session-thumb micro">
                    {thumbnail ? (
                      <img src={thumbnail} alt={video.title} loading="lazy" />
                    ) : (
                      <div className="thumb-placeholder">No art</div>
                    )}
                    <span className="session-badge index">#{index + 1}</span>
                    <span className="session-badge status" title={sessionState.status}>
                      {isActive ? "⏸" : "▶︎"}
                    </span>
                    <a
                      href={`https://youtube.com/watch?v=${videoId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="session-badge external"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ↗
                    </a>
                    {selectedIcon && (
                      <span className="session-badge selected" title="Last selected">
                        {selectedIcon}
                      </span>
                    )}
                  </div>
                  {shouldMountPlayer && (
                    <div className="hidden-player" aria-hidden="true">
                      <YouTube
                        videoId={videoId}
                        opts={YT_PLAYER_OPTIONS}
                        onReady={(event) => handlePlayerReady(videoId, event)}
                        onStateChange={(event) => handlePlayerStateChange(videoId, event)}
                        onError={(event) =>
                          console.error("YouTube DJ session error", videoId, event?.data)
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Music;
